"use client";

/**
 * Вертикальная форма поиска (место подачи + даты/время + кнопка) — вынесена из
 * CarDetailClient, чтобы её переиспользовала и страница «Темы поездок»
 * (/themes/[slug]): там тот же поиск, но по городу темы, без привязки к машине.
 * Дизайн и логика 1:1 с порта SearchFormPlaceholder Солнышко.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import RangeDateTimePicker from "@/components/RangeDateTimePicker";
import { useLocale } from "@/lib/locale-context";
import { countries } from "@/lib/countries";
import { fetchDeliveryPlaces, searchCars, type DeliveryPlace, type ApiCar } from "@/lib/api";
import { track } from "@/lib/metrika";

/* ── Хелперы дат/времени (порт SearchFormPlaceholder Солнышко) ── */

export function toISO(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
export function addDays(iso: string, days: number) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + days);
  return toISO(d);
}
// Дефолтное время старта. Для «сегодня» — (сейчас + 2 ч), округлённое вверх до
// четверти часа; иначе 12:00. Если +2ч перевалило за полночь — 23:45.
export function defaultStartTime(dateISO: string): string {
  if (!dateISO || dateISO !== toISO(new Date())) return "12:00";
  const d = new Date(Date.now() + 2 * 60 * 60 * 1000);
  const minutes = Math.ceil(d.getMinutes() / 15) * 15;
  d.setMinutes(minutes, 0, 0);
  if (d.getDate() !== new Date().getDate()) return "23:45";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
// Срок аренды (число суток между датами) — ceil от (end - start), минимум 1.
export function rentalDaysBetween(startISO: string, endISO: string): number {
  if (!startISO || !endISO) return 1;
  const a = new Date(startISO + "T00:00:00").getTime();
  const b = new Date(endISO + "T00:00:00").getTime();
  const days = Math.ceil((b - a) / 86_400_000);
  return Math.max(1, days);
}
export function formatDateDisplay(isoDate: string): string {
  if (!isoDate) return "";
  const d = new Date(isoDate + "T00:00:00");
  const monthsShortRu = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
  return `${d.getDate()} ${monthsShortRu[d.getMonth()]}, ${d.getFullYear()}`;
}

/* ── Хелперы телефона/стран (используются BookingPanel в CarDetailClient) ── */

export const POPULAR_CODES = ["RU", "TH", "KZ", "BY", "UA", "UZ", "GE", "AM", "TR", "AE"];
export const SORTED_COUNTRIES = [
  ...POPULAR_CODES.map((code) => countries.find((c) => c.code === code)!).filter(Boolean),
  ...countries.filter((c) => !POPULAR_CODES.includes(c.code)),
];
export function applyMask(value: string, mask?: string): string {
  if (!mask) return value;
  const digits = value.replace(/\D/g, "").slice(0, 15); // E.164: максимум 15 цифр
  let result = "";
  let di = 0;
  for (let i = 0; i < mask.length && di < digits.length; i++) {
    if (mask[i] === "#") result += digits[di++];
    else result += mask[i];
  }
  // Номер длиннее маски (например DE: 10–11 цифр) — хвост дописываем, не режем.
  if (di < digits.length) result += digits.slice(di);
  return result;
}
export function getPlaceholder(mask?: string, fallback = "Номер телефона"): string {
  if (!mask) return fallback;
  let i = 0;
  const digits = "9876543210";
  return mask.replace(/#/g, () => digits[i++ % digits.length]);
}

/* Форма дат/места (порт SearchFormPlaceholder, vertical). */
export function VerticalSearchForm({
  station,
  office,
  currency,
  onSearch,
  presetStartDate,
  presetEndDate,
  presetStartTime,
  presetEndTime,
  lockedDeliveryGuid,
  autoSearchOnce,
  submitLabel,
}: {
  station: string;
  office?: string;
  currency: string;
  onSearch: (cars: ApiCar[], days: number, from: string, to: string, st: string, et: string) => void;
  presetStartDate?: string;
  presetEndDate?: string;
  presetStartTime?: string;
  presetEndTime?: string;
  lockedDeliveryGuid?: string;
  autoSearchOnce?: boolean;
  submitLabel?: string;
}) {
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [openCal, setOpenCal] = useState<"start" | "end" | null>(null);
  const [searching, setSearching] = useState(false);
  const { locale } = useLocale();
  const [deliveryPlaces, setDeliveryPlaces] = useState<DeliveryPlace[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<DeliveryPlace | null>(null);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [deliveryHover, setDeliveryHover] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const deliveryRef = useRef<HTMLDivElement>(null);

  const placeError = showErrors && !selectedPlace;
  const startError = showErrors && !startDate;
  const endError = showErrors && !endDate;
  const closeCal = useCallback(() => setOpenCal(null), []);

  // Места подачи города.
  useEffect(() => {
    if (!station) return;
    let cancelled = false;
    fetchDeliveryPlaces(station, office)
      .then((data) => {
        if (cancelled) return;
        const places = data.cities;
        setDeliveryPlaces(places);
        const preferred = places[0];
        if (lockedDeliveryGuid) {
          const match = places.find((p) => p.guid === lockedDeliveryGuid);
          if (match) setSelectedPlace(match);
          else if (places.length > 0) setSelectedPlace((curr) => curr || preferred);
        } else if (places.length > 0) {
          setSelectedPlace((curr) => curr || preferred);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [station, office, lockedDeliveryGuid]);

  // Пресет дат из URL.
  useEffect(() => {
    if (presetStartDate || presetEndDate) {
      if (presetStartDate) setStartDate(presetStartDate);
      if (presetEndDate) setEndDate(presetEndDate);
      setStartTime(presetStartTime || defaultStartTime(presetStartDate || ""));
      setEndTime(presetEndTime || "12:00");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetStartDate, presetEndDate, presetStartTime, presetEndTime]);

  // Диапазонный выбор дат — клампы как у Солнышко.
  const handleRangeChange = (start: string, end: string) => {
    const today = toISO(new Date());
    if (start < today) start = today;
    setStartDate(start);
    const isToday = start === today;
    if (isToday) {
      const floor = defaultStartTime(start);
      if (!startTime || startTime < floor) setStartTime(floor);
    } else if (!startTime) {
      setStartTime(defaultStartTime(start));
    }
    const minEnd = addDays(start, 1);
    let e = end || minEnd;
    if (e < minEnd) e = minEnd;
    setEndDate(e);
    if (!endTime) setEndTime("12:00");
  };

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (deliveryRef.current && !deliveryRef.current.contains(e.target as Node)) setDeliveryOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const runSearch = useCallback(async () => {
    if (!selectedPlace || !startDate || !endDate) return;
    const st = startTime || defaultStartTime(startDate);
    const et = endTime || "12:00";
    // Тот же день разрешён — минимум 4 часа между подачей и возвратом.
    if (endDate < startDate) return;
    if (endDate === startDate) {
      const mins = (t: string) => { const [h, m] = (t || "12:00").split(":").map(Number); return h * 60 + m; };
      if (mins(et) - mins(st) < 240) return;
    }
    setSearching(true);
    try {
      const cars = await searchCars({ station, startDate, startTime: st, endDate, endTime: et });
      const days = rentalDaysBetween(startDate, endDate);
      track("search_submit_page", { city: station });
      onSearch(cars, days, startDate, endDate, st, et);
      if (typeof window !== "undefined") {
        try {
          const cur = new URL(window.location.href);
          cur.searchParams.set("start", startDate);
          cur.searchParams.set("end", endDate);
          cur.searchParams.set("start_time", st);
          cur.searchParams.set("end_time", et);
          if (selectedPlace?.guid) cur.searchParams.set("delivery", selectedPlace.guid);
          window.history.replaceState({}, "", cur.toString());
        } catch {}
      }
    } catch {
      onSearch([], 0, startDate, endDate, st, et);
    } finally {
      setSearching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlace, startDate, endDate, startTime, endTime, station]);

  // Авто-поиск один раз при пресете дат.
  const lastAutoKey = useRef("");
  useEffect(() => {
    if (!selectedPlace || !startDate || !endDate) return;
    const hasPresetDates = !!presetStartDate || !!presetEndDate;
    if (!hasPresetDates) return;
    if (autoSearchOnce) {
      if (lastAutoKey.current) return;
      lastAutoKey.current = "once";
      runSearch();
    }
  }, [selectedPlace, startDate, endDate, presetStartDate, presetEndDate, runSearch, autoSearchOnce]);

  const datePlaceholder = locale === "en" ? "Select a date" : "Выберите дату";
  const timePlaceholder = "--:--";
  const gridCols = "minmax(0, 1fr)";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        background: "#FFFFFF",
        borderRadius: 12,
        padding: "0",
        gap: 12,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      <style>{`
        .sfp-field { display: flex; align-items: center; height: 52px; background: #fff; border: 1px solid #E2E6EE; border-radius: 12px; box-sizing: border-box; position: relative; }
        .sfp-field-value { flex: 1; font-size: 15px; padding-left: 8px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .sfp-field-time { font-size: 15px; }
        .sfp-divider { width: 1px; height: 24px; background: #E2E6EE; margin-left: 8px; }
        @keyframes sfp-spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Inputs */}
      <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 12 }}>
        {/* Delivery place */}
        <div
          ref={deliveryRef}
          className="sfp-field"
          style={{ cursor: "pointer", border: placeError ? "1px solid #E53935" : undefined }}
          onClick={() => { setDeliveryOpen(!deliveryOpen); setOpenCal(null); }}
          onMouseEnter={() => setDeliveryHover(true)}
          onMouseLeave={() => setDeliveryHover(false)}
        >
          {/* map-pin */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ marginLeft: 12, flexShrink: 0 }}>
            <path d="M12 21s7-6.5 7-11a7 7 0 1 0-14 0c0 4.5 7 11 7 11Z" stroke="#0039A6" strokeWidth="2" strokeLinejoin="round" />
            <circle cx="12" cy="10" r="2.5" stroke="#0039A6" strokeWidth="2" />
          </svg>
          <span
            className="sfp-field-value"
            title={selectedPlace ? (locale === "en" && selectedPlace.name_en ? selectedPlace.name_en : selectedPlace.name) : ""}
            style={{ color: selectedPlace ? "#000" : "#AAA" }}
          >
            {selectedPlace ? (locale === "en" && selectedPlace.name_en ? selectedPlace.name_en : selectedPlace.name) : (locale === "en" ? "Pickup location" : "Место подачи")}
          </span>
          <svg width="12" height="12" viewBox="0 0 12 12" style={{ marginRight: 12, flexShrink: 0, transform: deliveryOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>
            <path d="M2 4L6 8L10 4" stroke="#999" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {/* Тултип полного названия (длинные имена не влезают в поле) */}
          {deliveryHover && !deliveryOpen && selectedPlace && (
            <div style={{
              position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 200,
              background: "#141d45", color: "#fff", fontSize: 13, lineHeight: 1.3,
              padding: "8px 12px", borderRadius: 8, boxShadow: "0 4px 12px rgba(20,29,69,0.25)",
              pointerEvents: "none", maxWidth: "min(360px, calc(100vw - 60px))", wordBreak: "break-word",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <span>{locale === "en" && selectedPlace.name_en ? selectedPlace.name_en : selectedPlace.name}</span>
              {(selectedPlace.price || 0) > 0 && (
                <span style={{ color: "#9DBDF2", fontWeight: 600, whiteSpace: "nowrap" }}>+{(selectedPlace.price || 0).toLocaleString("ru-RU")} {currency}</span>
              )}
            </div>
          )}
          {deliveryOpen && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                left: 0,
                background: "#fff",
                borderRadius: 10,
                boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                zIndex: 100,
                maxHeight: 300,
                overflowY: "auto",
                minWidth: "100%",
                width: "max(100%, 340px)",
                maxWidth: "min(420px, calc(100vw - 60px))",
              }}
            >
              {deliveryPlaces.map((place) => {
                const active = selectedPlace?.guid === place.guid;
                return (
                  <button
                    key={place.guid}
                    type="button"
                    onClick={() => { track("pickup_select", { city: station }); setSelectedPlace(place); setDeliveryOpen(false); setShowErrors(false); }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "10px 16px",
                      border: "none",
                      background: active ? "#dfe8f6" : "#fff",
                      cursor: "pointer",
                      fontSize: 13,
                      color: "#2B2F33",
                      textAlign: "left",
                      transition: "background 0.1s",
                      fontFamily: "inherit",
                    }}
                    title={locale === "en" && place.name_en ? place.name_en : place.name}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "#f5f5f5"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = active ? "#dfe8f6" : "#fff"; }}
                  >
                    <span style={{ lineHeight: 1.35, wordBreak: "break-word" }}>{locale === "en" && place.name_en ? place.name_en : place.name}</span>
                    <span style={{ color: "#0039A6", fontWeight: 600, fontSize: 13, marginLeft: 8, whiteSpace: "nowrap" }}>
                      {(place.price || 0) > 0
                        ? `+${(place.price || 0).toLocaleString("ru-RU")} ${currency}`
                        : (locale === "en" ? "Free" : "Бесплатно")}
                    </span>
                  </button>
                );
              })}
              {deliveryPlaces.length === 0 && (
                <div style={{ padding: "12px 16px", color: "#AAA", fontSize: 13 }}>{locale === "en" ? "No pickup locations" : "Нет мест подачи"}</div>
              )}
            </div>
          )}
        </div>

        {/* Start date + time */}
        <div className="sfp-field" style={{ cursor: "pointer", border: startError ? "1px solid #E53935" : undefined }} onClick={() => { setOpenCal(openCal === "start" ? null : "start"); setDeliveryOpen(false); }}>
          {/* calendar */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ marginLeft: 12, flexShrink: 0 }}>
            <rect x="3" y="4.5" width="18" height="16" rx="2" stroke="#0039A6" strokeWidth="1.8" />
            <path d="M3 9h18M8 3v3M16 3v3" stroke="#0039A6" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <span className="sfp-field-value" style={{ color: startDate ? "#000" : "#AAA" }}>
            {startDate ? formatDateDisplay(startDate) : datePlaceholder}
          </span>
          <div className="sfp-divider" />
          {/* clock */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginLeft: 8, flexShrink: 0 }}>
            <circle cx="12" cy="12" r="9" stroke="#0039A6" strokeWidth="1.8" />
            <path d="M12 7v5l3.5 2" stroke="#0039A6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="sfp-field-time" style={{ color: startDate ? "#000" : "#AAA", minWidth: 56, textAlign: "center", display: "inline-block", marginRight: 8 }}>
            {startDate ? (startTime || defaultStartTime(startDate)) : timePlaceholder}
          </span>
          {openCal === "start" && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ position: "absolute", left: 0, top: "calc(100% + 8px)", zIndex: 100, overflow: "hidden", borderRadius: 16, background: "#fff", boxShadow: "0 16px 40px rgba(20,29,69,0.2)", border: "1px solid #E0E4EC" }}
            >
              <RangeDateTimePicker
                startISO={startDate}
                endISO={endDate}
                startTime={startTime}
                endTime={endTime}
                minDate={toISO(new Date())}
                minTime={defaultStartTime(toISO(new Date()))}
                minDays={1}
                onRangeChange={handleRangeChange}
                onStartTime={setStartTime}
                onEndTime={setEndTime}
                onClose={closeCal}
              />
            </div>
          )}
        </div>

        {/* End date + time */}
        <div className="sfp-field" style={{ cursor: "pointer", border: endError ? "1px solid #E53935" : undefined }} onClick={() => { setOpenCal(openCal === "end" ? null : "end"); setDeliveryOpen(false); }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ marginLeft: 12, flexShrink: 0 }}>
            <rect x="3" y="4.5" width="18" height="16" rx="2" stroke="#0039A6" strokeWidth="1.8" />
            <path d="M3 9h18M8 3v3M16 3v3" stroke="#0039A6" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <span className="sfp-field-value" style={{ color: endDate ? "#000" : "#AAA" }}>
            {endDate ? formatDateDisplay(endDate) : datePlaceholder}
          </span>
          <div className="sfp-divider" />
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginLeft: 8, flexShrink: 0 }}>
            <circle cx="12" cy="12" r="9" stroke="#0039A6" strokeWidth="1.8" />
            <path d="M12 7v5l3.5 2" stroke="#0039A6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span className="sfp-field-time" style={{ color: endDate ? "#000" : "#AAA", minWidth: 56, textAlign: "center", display: "inline-block", marginRight: 8 }}>
            {endDate ? (endTime || "12:00") : timePlaceholder}
          </span>
          {openCal === "end" && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", zIndex: 100, overflow: "hidden", borderRadius: 16, background: "#fff", boxShadow: "0 16px 40px rgba(20,29,69,0.2)", border: "1px solid #E0E4EC" }}
            >
              <RangeDateTimePicker
                startISO={startDate}
                endISO={endDate}
                startTime={startTime}
                endTime={endTime}
                minDate={toISO(new Date())}
                minTime={defaultStartTime(toISO(new Date()))}
                minDays={1}
                onRangeChange={handleRangeChange}
                onStartTime={setStartTime}
                onEndTime={setEndTime}
                onClose={closeCal}
              />
            </div>
          )}
        </div>

        {/* Search button */}
        <button
          type="button"
          disabled={searching}
          onClick={async () => {
            if (!selectedPlace || !startDate || !endDate) {
              setShowErrors(true);
              return;
            }
            await runSearch();
          }}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#0039A6",
            borderRadius: 10,
            border: "none",
            cursor: "pointer",
            transition: "background 0.15s",
            minHeight: 52,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#032f8a"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#0039A6"; }}
        >
          {searching ? (
            <span
              aria-label="loading"
              style={{
                width: 18, height: 18, display: "inline-block",
                border: "2.5px solid rgba(255,255,255,0.45)", borderTopColor: "#FFFFFF",
                borderRadius: "50%", animation: "sfp-spin 0.7s linear infinite",
              }}
            />
          ) : (
            <span style={{ color: "#FFFFFF", fontSize: 15, fontWeight: "bold" }}>
              {submitLabel || (locale === "en" ? "Book" : "Забронировать")}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}
